import { round } from "lodash";
import React from "react";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import ScoreChart, { ScoreChartProps } from "../ScoreChart";
import { AvatarWithInitials, AvatarWithScore } from "@/shared/ui/Avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { Row } from "@/shared/ui/Blocks";
import { PersonScore } from "@/features/people/hooks/useDimensionsWithScores";

interface PeopleCompareScoreChartProps
  extends Omit<ScoreChartProps, "renderScore"> {
  highlightedPersonId?: number;
}

function PersonTooltip({
  score,
  children,
}: {
  score: PersonScore;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger className="bg-transparent rounded-full">
        {children}
      </TooltipTrigger>
      <TooltipContent className="hover:z-9990">
        <Row className="bg-black rounded-md">
          <p className="text-white">
            {score?.person?.name} <b>{round(score.value)}</b>
          </p>
        </Row>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}

function PeopleCompareScoreChart(props: PeopleCompareScoreChartProps) {
  const { highlightedPersonId, ...rest } = props;

  const renderScore = (score: PersonScore) => {
    const key = score.person?.id + score.key;
    if (score.person?.id === highlightedPersonId) {
      return (
        <PersonTooltip score={score} key={key}>
          <AvatarWithScore
            avatar={score.person?.avatar || ""}
            initials={score?.person?.initials || ""}
            isContact={!!score.person?.is_contact}
            name={score?.person?.name || ""}
            score={round(score?.value)}
            size="md"
          />
        </PersonTooltip>
      );
    }
    return (
      <PersonTooltip score={score} key={key}>
        <AvatarWithInitials
          avatar={score.person?.avatar || ""}
          initials={score?.person?.initials || ""}
          isContact={!!score.person?.is_contact}
          size="xs"
        />
      </PersonTooltip>
    );
  };

  return <ScoreChart renderScore={renderScore} {...rest} />;
}

export { PeopleCompareScoreChart };
